import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Upload, Sparkles, Star, ChevronRight, X, TrendingUp, Award, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/api";
import { useAuth } from "@/context/AuthContext";
import PaywallModal from "@/components/PaywallModal";
import AuthModal from "@/components/AuthModal";
import PersonalizedHome from "@/components/PersonalizedHome";

const ANON_FREE_KEY = "nail_anon_free_used";
const ADMIN_MODE_KEY = "nail_rater_admin_mode";
const ADMIN_SECRET = "Hexgo-NipobjqvKeCvbYxCk4abz-Rarg";

export default function HomePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refresh } = useAuth();
  const fileInputRef = useRef(null);

  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [anonUsed, setAnonUsed] = useState(0);
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [uploadMode, setUploadMode] = useState(false);

  useEffect(() => {
    const adminParam = searchParams.get("admin");
    if (adminParam === ADMIN_SECRET) {
      localStorage.setItem(ADMIN_MODE_KEY, "true");
      toast.success("🔓 Admin mode activated! Unlimited testing enabled.");
    }
    setIsAdminMode(localStorage.getItem(ADMIN_MODE_KEY) === "true");
    setAnonUsed(parseInt(localStorage.getItem(ANON_FREE_KEY) || "0", 10));
  }, [searchParams]);

  useEffect(() => {
    // Ensure logged-in users land on the personalized home, not a stale uploader
    if (user) setUploadMode(false);
  }, [user]);

  const subscribed = user?.is_subscribed || isAdminMode;
  const freeRemaining = subscribed ? Infinity : (user ? user.free_remaining : Math.max(0, 1 - anonUsed));

  // Returns: 'allow' | 'signup' | 'paywall'
  const gate = () => {
    if (subscribed) return "allow";
    if (user) return user.free_remaining > 0 ? "allow" : "paywall";
    return anonUsed < 1 ? "allow" : "signup";
  };

  const enforceGate = () => {
    const g = gate();
    if (g === "signup") { setShowAuth(true); return false; }
    if (g === "paywall") { setShowPaywall(true); return false; }
    return true;
  };

  const handleFileSelect = useCallback((file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please upload an image file (JPG or PNG)"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image too large. Please use an image under 10MB"); return; }
    setSelectedImage(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
  }, []);

  const handleUploadZoneClick = () => {
    if (!enforceGate()) return;
    if (!imagePreview) fileInputRef.current?.click();
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    if (!enforceGate()) return;
    handleFileSelect(e.dataTransfer.files[0]);
  }, [handleFileSelect, user, anonUsed, isAdminMode]);

  const clearImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleAnalyze = async () => {
    if (!enforceGate()) return;
    if (!selectedImage) { toast.error("Please upload a photo of your nails first"); return; }

    setIsAnalyzing(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result.split(",")[1];
      try {
        const { data: result } = await api.post("/analyze", { image_base64: base64, savage_mode: false });

        if (!user && !subscribed) {
          const next = anonUsed + 1;
          localStorage.setItem(ANON_FREE_KEY, next.toString());
          setAnonUsed(next);
        }

        let updated = user;
        if (user) updated = await refresh();

        const remainingAfter = subscribed ? Infinity : (updated ? updated.free_remaining : 0);

        navigate("/results", {
          state: {
            rating: result,
            imagePreview,
            promptSignup: !user && !subscribed,
            promptPaywall: !!user && !subscribed && remainingAfter <= 0,
          },
        });
      } catch (error) {
        if (error?.response?.status === 402) {
          setShowPaywall(true);
        } else {
          toast.error("Failed to analyze your nails. Please try again.");
        }
        setIsAnalyzing(false);
      }
    };
    reader.readAsDataURL(selectedImage);
  };

  const counterText = () => {
    if (isAdminMode) return "🔓 Admin Mode – Unlimited testing";
    if (subscribed) return "Unlimited analyses unlocked ✨";
    if (user) return <>You have <span className="text-primary font-bold">{user.free_remaining}</span> free analysis{user.free_remaining !== 1 ? "es" : ""} left</>;
    return <>You have <span className="text-primary font-bold">{Math.max(0, 1 - anonUsed)}</span> free analysis left</>;
  };

  if (user && !uploadMode) {
    return (
      <>
        <PersonalizedHome onNewAnalysis={() => setUploadMode(true)} />
        <PaywallModal isOpen={showPaywall} onClose={() => setShowPaywall(false)} />
      </>
    );
  }

  return (
    <div className="min-h-screen">
      <section className="min-h-[calc(100vh-4rem)] flex flex-col items-center justify-center px-4 py-12 relative overflow-hidden">
        {user && (
          <button onClick={() => { setUploadMode(false); clearImage(); }} className="absolute top-4 left-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-home-btn">
            <ChevronRight className="w-4 h-4 rotate-180" /> Home
          </button>
        )}
        <motion.div className="absolute top-20 left-10 w-20 h-20 rounded-full bg-pink-200/50 blur-2xl" animate={{ y: [0, -20, 0], scale: [1, 1.1, 1] }} transition={{ duration: 4, repeat: Infinity }} />
        <motion.div className="absolute bottom-32 right-10 w-32 h-32 rounded-full bg-purple-200/50 blur-2xl" animate={{ y: [0, 20, 0], scale: [1, 1.2, 1] }} transition={{ duration: 5, repeat: Infinity }} />

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center max-w-2xl mx-auto">
          <motion.div className="mb-4" initial={{ scale: 0.9 }} animate={{ scale: 1 }} transition={{ duration: 0.5 }}>
            <span className="inline-block text-xs tracking-widest uppercase text-primary font-semibold mb-2">Your personal AI Nail Coach</span>
            <h1 className="font-playfair text-5xl md:text-7xl font-bold text-foreground tracking-tight">Nail Coach</h1>
            <div className="flex items-center justify-center gap-1 mt-2">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-pink-400 text-pink-400" />)}
            </div>
          </motion.div>

          <p className="text-lg md:text-xl text-muted-foreground mb-8 font-medium">
            Upload your mani, get an <span className="text-primary font-semibold">honest AI rating</span> and a personalized plan to improve every week
          </p>

          <motion.div className="glass rounded-full px-4 py-2 inline-flex items-center gap-2 mb-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} data-testid="free-counter">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">{counterText()}</span>
          </motion.div>

          <motion.div
            className={`upload-zone glass-heavy rounded-3xl p-8 md:p-12 cursor-pointer mb-8 ${isDragging ? "dragging" : ""}`}
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onClick={handleUploadZoneClick}
            whileHover={{ scale: imagePreview ? 1 : 1.02 }}
            data-testid="upload-zone"
          >
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => handleFileSelect(e.target.files[0])} className="hidden" data-testid="file-input" />
            <AnimatePresence mode="wait">
              {imagePreview ? (
                <motion.div key="preview" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="relative">
                  <img src={imagePreview} alt="Nail preview" className="max-h-64 md:max-h-80 rounded-2xl mx-auto object-contain shadow-lg" data-testid="image-preview" />
                  <button onClick={(e) => { e.stopPropagation(); clearImage(); }} className="absolute -top-3 -right-3 bg-primary text-white rounded-full p-2 shadow-lg" data-testid="clear-image-btn">
                    <X className="w-4 h-4" />
                  </button>
                </motion.div>
              ) : (
                <motion.div key="upload" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-4">
                  <div className="flex gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center"><Camera className="w-8 h-8 text-primary" /></div>
                    <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center"><Upload className="w-8 h-8 text-primary" /></div>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Drop your nail photo here</p>
                    <p className="text-sm text-muted-foreground mt-1">or tap to upload • JPG, PNG</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
            <Button onClick={handleAnalyze} disabled={!selectedImage || isAnalyzing} className="rounded-full px-8 py-6 text-lg font-semibold shadow-soft hover:shadow-glow transition-all bg-primary hover:bg-primary/90 disabled:opacity-50" data-testid="analyze-btn">
              {isAnalyzing ? (
                <span className="analyzing-text flex items-center gap-2"><Sparkles className="w-5 h-5 animate-spin" />Analyzing your nails...</span>
              ) : (
                <span className="flex items-center gap-2">Analyze My Nails<ChevronRight className="w-5 h-5" /></span>
              )}
            </Button>
          </motion.div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.6 }} className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl w-full">
          {[
            { icon: Sparkles, t: "Honest AI rating", d: "Scored out of 20 across 4 expert categories" },
            { icon: TrendingUp, t: "Track progress", d: "Weekly score evolution & before/after" },
            { icon: Award, t: "Stay motivated", d: "Streaks, badges & weekly challenges" },
          ].map((f, i) => (
            <div key={i} className="glass rounded-2xl p-5 text-left">
              <f.icon className="w-6 h-6 text-primary mb-2" />
              <p className="font-semibold text-foreground">{f.t}</p>
              <p className="text-sm text-muted-foreground mt-1">{f.d}</p>
            </div>
          ))}
        </motion.div>

        <motion.button
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
          whileHover={{ scale: 1.02 }}
          onClick={() => navigate("/occasions")}
          className="mt-6 max-w-2xl w-full glass-heavy rounded-2xl p-5 flex items-center justify-between gap-4 text-left"
          data-testid="home-occasions-cta"
        >
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center"><Wand2 className="w-5 h-5 text-primary" /></span>
            <div>
              <p className="font-semibold text-foreground">Not sure what nails to get next?</p>
              <p className="text-sm text-muted-foreground">Get an AI look for any occasion — wedding, date, office & more</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-primary shrink-0" />
        </motion.button>
      </section>

      <PaywallModal isOpen={showPaywall} onClose={() => setShowPaywall(false)} />
      <AuthModal
        isOpen={showAuth}
        mode="signup"
        title="Loved your rating? Create a free account"
        subtitle="Get one more free analysis + start tracking your nail journey"
        onClose={() => setShowAuth(false)}
      />
    </div>
  );
}
